<template>
  <WCToast
    class="wc-toast-modern"
    :class="[`wc-toast-modern--${variant}`, { 'wc-toast-modern--full-width': fullWidth }]"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
  >
    <WCToastHeader>
      <font-awesome-icon :icon="variantIcon" class="alert-icon" aria-hidden="true" />
      <strong class="alert-text">{{ message }}</strong>
      <button type="button" class="alert-close" :aria-label="$t('close')" @click="dismiss">
        <font-awesome-icon :icon="['fas', 'xmark']" aria-hidden="true" />
      </button>
    </WCToastHeader>
  </WCToast>
</template>

<script setup lang="ts">
import { computed, defineProps, withDefaults } from 'vue';
import WCToast from '@/modules/toasts/components/WCToast/WCToast.vue';
import WCToastHeader from '@/modules/toasts/components/WCToastHeader/WCToastHeader.vue';

interface Props {
  variant: 'warning' | 'danger' | 'success' | 'info';
  message: string;
  fullWidth?: boolean;
  dismiss?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: false,
  dismiss: () => {},
});

const variantIcon = computed(() => {
  const map: Record<string, string[]> = {
    warning: ['fas', 'triangle-exclamation'],
    danger: ['fas', 'circle-exclamation'],
    success: ['fas', 'circle-check'],
    info: ['fas', 'circle-info'],
  };
  return map[props.variant] ?? map.warning;
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-toast-modern {
  position: static;
  display: block;
  width: 100%;
  max-width: convert-px-to-rem(350);
  margin-left: auto;
  flex: 0 0 auto;
  background: transparent;
  border: none;
  box-shadow: none;
  border-radius: 0.375rem;
  overflow: hidden;
}

.wc-toast-modern--full-width {
  max-width: none;

  :deep(.toast-header) {
    display: flex;
    align-items: center;
    gap: convert-px-to-rem(10);
    padding: convert-px-to-rem(10) convert-px-to-rem(14);
    font-size: 0.875rem;
    background: transparent;
    border: none;
    border-radius: 0;
    color: $black;
  }
}

.wc-toast-modern--warning {
  :deep(.toast-header) {
    background: var(--warning-lighter-9, get-color('warning', 'lighter-9'));
  }
  :deep(.alert-icon) {
    color: var(--warning-darker-3, get-color('warning'));
  }
}

.wc-toast-modern--danger {
  :deep(.toast-header) {
    background: var(--danger-lighter-9, get-color('danger', 'lighter-9'));
  }
  :deep(.alert-icon) {
    color: var(--danger, get-color('danger'));
  }
}

.wc-toast-modern--success {
  :deep(.toast-header) {
    background: var(--success-lighter-9, get-color('success', 'lighter-9'));
  }
  :deep(.alert-icon) {
    color: var(--success, get-color('success'));
  }
}

.wc-toast-modern--info {
  :deep(.toast-header) {
    background: var(--info-lighter-9, get-color('info', 'lighter-9'));
  }
  :deep(.alert-icon) {
    color: var(--info, get-color('info'));
  }
}

.alert-icon {
  flex-shrink: 0;
}

.alert-text {
  flex: 1;
  font-weight: 400;
  line-height: 1.4;
  color: $dark;
  font-size: $font-size-sm;
}

.alert-close {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--dark-lighter-4, get-color('dark', 'lighter-4'));
  padding: convert-px-to-rem(2);
  line-height: 1;

  &:hover {
    color: var(--dark, get-color('dark'));
  }
}
</style>
